import { useCallback, useEffect, useMemo } from 'react';
import { Form, message } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useClosingPackageCommands } from './useClosingPackageCommands';
import { useClosingPackageTaskModalData } from './useClosingPackageTaskModalData';
import { buildCompositionEditorRows } from '../lib/compositionEditorAdapter';
import { resolveCompositionEditGate } from '../lib/compositionEditGate';
import {
  buildRevisionConfirmBody,
  getRevisionSuccessMessage,
  validateRevisionSubmit,
} from '../lib/revisionModalValidation';
import { resolveRevisionRejectionReasonView } from '../lib/revisionModalRejectionReason';
import { mapClosingPackageApiError } from '../lib/errorMapping';
import { useClosingPackagePermissions } from './useClosingPackagePermissions';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store';
import { userHasClosingPackageRole } from '../permissions/closingPackagePermissions';

export type RevisionModalFormValues = {
  revisedAt: Dayjs;
  comment: string;
};

export type UseRevisionModalOptions = {
  applicationId: number;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
};

export function useRevisionModal({
  applicationId,
  open,
  onClose,
  onSuccess,
}: UseRevisionModalOptions) {
  const [form] = Form.useForm<RevisionModalFormValues>();
  const permissions = useClosingPackagePermissions();
  const user = useSelector((state: RootState) => state.auth.user);

  const { projection, composition, contextView, contextLoading } = useClosingPackageTaskModalData({
    applicationId,
    open,
    loadComposition: true,
  });

  const { confirmFormation, states } = useClosingPackageCommands();

  const allowedToEdit = useMemo(
    () => permissions.canEdit && userHasClosingPackageRole(user, ['admin', 'accountant']),
    [permissions.canEdit, user]
  );

  const validationRows = useMemo(() => {
    if (!projection) return [];
    const editGate = resolveCompositionEditGate({
      allowedToEdit,
      locked: projection.locked,
      formed: projection.formed,
      compositionMode: projection.compositionMode,
      workflowState: projection.workflowState,
    });
    return buildCompositionEditorRows({
      projection,
      composition: composition ?? null,
      canEdit: editGate.canEdit,
    });
  }, [allowedToEdit, composition, projection]);

  const rejectionReasonView = useMemo(
    () => resolveRevisionRejectionReasonView(projection),
    [projection]
  );

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }
    form.setFieldsValue({
      revisedAt: dayjs(),
      comment: '',
    });
  }, [form, open, applicationId]);

  const handleSubmit = useCallback(async () => {
    if (!projection) {
      message.error('Не удалось загрузить данные комплекта');
      return;
    }

    let values: RevisionModalFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const validationIssue = validateRevisionSubmit({
      form: {
        eventAt: values.revisedAt,
        comment: values.comment,
      },
      composition: {
        projection,
        composition,
        rows: validationRows,
      },
    });

    if (validationIssue) {
      message.error(validationIssue.message);
      return;
    }

    try {
      await confirmFormation({
        applicationId,
        body: buildRevisionConfirmBody(values.comment, values.revisedAt.toISOString()),
      }).unwrap();

      message.success(getRevisionSuccessMessage());
      await onSuccess?.();
      onClose();
    } catch (error) {
      message.error(mapClosingPackageApiError(error));
    }
  }, [
    applicationId,
    composition,
    confirmFormation,
    form,
    onClose,
    onSuccess,
    projection,
    validationRows,
  ]);

  return {
    form,
    projection,
    contextView,
    contextLoading,
    rejectionReasonView,
    submitting: states.confirmFormation.isLoading,
    handleSubmit,
  };
}

export type UseRevisionModalResult = ReturnType<typeof useRevisionModal>;
